<script setup lang="ts">
import type { MenuItemType } from '~/components/main/header/type';

withDefaults(
  defineProps<{
    variant?: `default` | `homepage` | `sticky`
  }>(),
  {
    variant: `default`,
  },
);

const { t } = useI18n();
const isMenuOpen = ref(false);
function toggleMenu() {
  isMenuOpen.value = !isMenuOpen.value;
  openedMenuType.value = undefined;
}
function closeMenu() {
  isMenuOpen.value = false;
  openedMenuType.value = undefined;
}

const htmlClass = computed(() => isMenuOpen.value ? `overflow-hidden` : ``);

useHeadSafe({
  htmlAttrs: {
    class: htmlClass,
  },
});

const openedMenuType = ref<MenuItemType>();
</script>

<template>
  <div :class="{ 'h-header': variant !== `homepage` }">
    <header
      class="w-full h-header px-m lg:px-xl z-20 lg:bg-white
        text-black border-b border-black/10 group-[.is-prestige]:font-title"
      :class="{
        'fixed': variant !== 'sticky',
        'header--homepage': variant === 'homepage' && !isMenuOpen,
        'bg-white': variant !== 'homepage' || isMenuOpen,
      }"
    >
      <div class="py-xs flex items-center h-full gap-28">
        <NuxtLinkLocale :to="{ name: 'index' }">
          <Icon
            name="iad-icon:logo-iad"
            class="size-10 text-secondary"
            :class="{
              'logo--homepage': variant === 'homepage' && !isMenuOpen,
            }"
            @click="isMenuOpen = false"
          />
        </NuxtLinkLocale>
        <nav
          class="flex-1 items-start lg:justify-between
            gap-xl lg:gap-2xl
            lg:static top-14 inset-0 z-20
            text-lg lg:text-base group-[.is-prestige]:text-base group-[.is-prestige]:lg:text-xs
            pt-l lg:pt-0 px-m lg:px-0
            bg-white lg:bg-transparent lg:flex flex-col lg:flex-row
            transition-transform lg:transition-none
            lg:drop-shadow-lg"
          :class="{
            'relative hidden': !isMenuOpen,
            'fixed flex animate-slideRightToLeft lg:animate-none': isMenuOpen,
          }"
        >
          <div class="flex gap-2xl flex-col lg:flex-row lg:flex w-full lg:w-auto justify-between lg:justify-start">
            <MainHeaderMenuItem
              v-model="openedMenuType"
              :text="t('core.navigation.buy')"
              type="buy"
              :variant
            >
              <MainHeaderSubmenuBuy @select="closeMenu" />
            </MainHeaderMenuItem>
            <MainHeaderMenuItem
              v-model="openedMenuType"
              :text="t('core.navigation.rent')"
              type="rent"
              :variant
            >
              <MainHeaderSubmenuRent @select="closeMenu" />
            </MainHeaderMenuItem>
            <MainHeaderMenuItem
              v-model="openedMenuType"
              :text="t('core.navigation.sell_estimate')"
              type="sell_estimate"
              :variant
            >
              <MainHeaderSubmenuSellEstimate @select="closeMenu" />
            </MainHeaderMenuItem>
            <MainHeaderMenuItemLink
              :text="t(`core.navigation.find_agent`)"
              :to="{ name: `find-real-estate-agent` }"
              menu-category1="collaborator_search"
              @click="closeMenu"
            />
            <MainHeaderMenuItem
              v-model="openedMenuType"
              :text="t('core.navigation.discover_iad')"
              type="discover_iad"
              :variant
            >
              <MainHeaderSubmenuDiscoverIad @select="closeMenu" />
            </MainHeaderMenuItem>
            <MainHeaderMenuItemLink
              :text="t(`core.navigation.become_agent`)"
              href="https://www.join-iad.fr/"
              target="_blank"
              menu-category1="become_collaborator"
              @click="closeMenu"
            />
          </div>
          <MainHeaderLangSwitch />
        </nav>
        <div class="flex gap-s ms-auto lg:hidden">
          <button
            class="z-20 flex items-center justify-center relative -mr-2xs"
            @click="toggleMenu"
          >
            <Icon
              :name="isMenuOpen ? 'iad-icon:close' : 'iad-icon:list'"
              class="size-10"
              :class="{
                'burger-menu--homepage': variant === 'homepage' && !isMenuOpen,
              }"
            />
          </button>
        </div>
      </div>
    </header>
  </div>
</template>

<style scoped>
@keyframes header-color {
  from {
    @apply bg-transparent border-b border-transparent;
  }

  to {
    @apply bg-white border-b border-black/10;
  }
}

@keyframes logo-color {
  from {
    @apply text-white;
  }

  to {
    @apply text-secondary;
  }
}

@keyframes burger-menu-color {
  from {
    @apply text-white;
  }

  to {
    @apply text-black;
  }
}

/* Apply scroll-linked animation ONLY for supported browsers */
@supports (animation-timeline: view()) {
  .header--homepage {
    animation: header-color auto linear both;
    animation-timeline: scroll();
    animation-range: 0% 50px;
  }

  .logo--homepage {
    animation: logo-color auto linear both;
    animation-timeline: scroll();
    animation-range: 0% 50px;
  }

  .burger-menu--homepage {
    animation: burger-menu-color auto linear both;
    animation-timeline: scroll();
    animation-range: 0% 50px;
  }
}

/* Fallback for Safari & Firefox (no animation-timeline support) */
@supports not (animation-timeline: view()) {
  .header--homepage {
    @apply bg-white border-b border-black/10;
  }

  .logo--homepage {
    @apply text-secondary;
  }

  .burger-menu--homepage {
    @apply text-black;
  }
}

@screen lg {

  .header--homepage,
  .logo--homepage {
    animation: none !important;
  }
}
</style>
